import { useRef } from 'react';
import styled from 'styled-components';

/**
 * The analysed range drawn over a FilmStrip: a green frame with a white handle at each end. Drag a handle (or use
 * the arrow keys on it) to move that end. Values are seconds.
 */

const Frame = styled.span`
	position: absolute;
	top: -4px;
	bottom: -4px;
	border: 2px solid ${({ theme }) => theme.colors.green};
	border-radius: 8px;
	background: rgba(32, 172, 107, 0.1);
	pointer-events: none;
`;

const Shade = styled.span`
	position: absolute;
	top: 0;
	bottom: 0;
	background: rgba(8, 10, 8, 0.6);
	pointer-events: none;
`;

const Handle = styled.button`
	position: absolute;
	top: 50%;
	width: 14px;
	height: 34px;
	margin: -17px 0 0 -7px;
	border: 0;
	border-radius: 4px;
	background: #fff;
	box-shadow: 0 0 0 3px rgba(32, 172, 107, 0.45);
	cursor: ew-resize;
	touch-action: none;
	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.colors.lightGreen};
		outline-offset: 2px;
	}
`;

const MIN_SPAN = 1;

const RangeOverlay = ({ duration, start, end, onChange }) => {
	const ref = useRef(null);
	if (!duration) return null;
	const pct = s => `${(Math.max(0, Math.min(duration, s)) / duration) * 100}%`;
	const set = (which, s) => {
		const v = Math.round(Math.max(0, Math.min(duration, s)) * 100) / 100;
		if (which === 'start') onChange({ start: Math.min(v, end - MIN_SPAN), end });
		else onChange({ start, end: Math.max(v, start + MIN_SPAN) });
	};
	const drag = which => e => {
		const box = ref.current?.parentElement?.getBoundingClientRect();
		if (!box) return;
		e.currentTarget.setPointerCapture(e.pointerId);
		const move = ev => set(which, ((ev.clientX - box.left) / box.width) * duration);
		const up = () => {
			window.removeEventListener('pointermove', move);
			window.removeEventListener('pointerup', up);
		};
		window.addEventListener('pointermove', move);
		window.addEventListener('pointerup', up);
	};
	const keys = which => e => {
		const step = e.shiftKey ? 5 : 0.5;
		const now = which === 'start' ? start : end;
		if (e.key === 'ArrowLeft') set(which, now - step);
		else if (e.key === 'ArrowRight') set(which, now + step);
		else return;
		e.preventDefault();
	};
	return (
		<span ref={ref} style={{ display: 'contents' }}>
			<Shade style={{ left: 0, width: pct(start) }} />
			<Shade style={{ left: pct(end), right: 0 }} />
			<Frame style={{ left: pct(start), width: `calc(${pct(end)} - ${pct(start)})` }} />
			{['start', 'end'].map(which => (
				<Handle
					key={which}
					type="button"
					role="slider"
					aria-label={
						which === 'start'
							? 'Start of the analysed part'
							: 'End of the analysed part'
					}
					aria-valuemin={0}
					aria-valuemax={Math.round(duration)}
					aria-valuenow={Math.round(which === 'start' ? start : end)}
					style={{ left: pct(which === 'start' ? start : end) }}
					onPointerDown={drag(which)}
					onKeyDown={keys(which)}
				/>
			))}
		</span>
	);
};

export default RangeOverlay;
